import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { ProductNavIcon } from './redesign/icons';
import { productDropdownLinks, resourceDropdownLinks } from './redesign/data';
import { useAuth } from '../context/AuthContext';
import '../pages/HomePage.css';

const isInternalHref = (href) => typeof href === 'string' && href.startsWith('/');

function NavHref({ href, className, onClick, children }) {
  if (isInternalHref(href)) {
    return (
      <Link to={href} className={className} onClick={onClick}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} className={className} onClick={onClick} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

function DockClawMark({ dark = false }) {
  return (
    <Link to="/" className={`paper-logo ${dark ? 'paper-logo-dark' : ''}`} aria-label="DockClaw home">
      <img src="/dlogoblack.svg" alt="DockClaw" className="paper-logo-full" />
    </Link>
  );
}

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const isLoggedIn = Boolean(token);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navRef = useRef(null);

  const handleLogout = useCallback(async () => {
    setMobileOpen(false);
    await logout();
    navigate('/');
  }, [logout, navigate]);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!openDropdown) return undefined;

    const handlePointerDown = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpenDropdown(null);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openDropdown]);

  const dropdowns = {
    product: productDropdownLinks,
    resources: resourceDropdownLinks,
  };

  const renderDropdown = (key) => (
    <div className={`paper-nav-dropdown ${openDropdown === key ? 'is-open' : ''}`}>
      <div className="paper-nav-dropdown-panel">
        {dropdowns[key].map((link) => (
          <NavHref
            key={link.label}
            href={link.href}
            className="paper-nav-dropdown-link"
            onClick={() => setOpenDropdown(null)}
          >
            <span className="paper-nav-dropdown-icon">
              <ProductNavIcon type={link.icon} />
            </span>
            <span>
              <b>{link.label}</b>
              <small>{link.subtext}</small>
            </span>
          </NavHref>
        ))}
      </div>
    </div>
  );

  return (
    <header className="paper-nav" ref={navRef}>
      <div className="paper-nav-inner">
        <DockClawMark />
        <nav className="paper-nav-links" aria-label="Primary navigation">
          <div className="paper-nav-item">
            <button
              type="button"
              className="paper-nav-link"
              aria-expanded={openDropdown === 'product'}
              onClick={() => setOpenDropdown((value) => (value === 'product' ? null : 'product'))}
            >
              <span>Product</span>
              <ChevronDown size={12} />
            </button>
            {renderDropdown('product')}
          </div>
          <Link to="/workflows" className="paper-nav-link">Workflows</Link>
          <div className="paper-nav-item">
            <button
              type="button"
              className="paper-nav-link"
              aria-expanded={openDropdown === 'resources'}
              onClick={() => setOpenDropdown((value) => (value === 'resources' ? null : 'resources'))}
            >
              <span>Resources</span>
              <ChevronDown size={12} />
            </button>
            {renderDropdown('resources')}
          </div>
          <Link to="/pricing" className="paper-nav-link">Pricing</Link>
          <Link to="/contact" className="paper-nav-link">Contact</Link>
        </nav>
        <div className="paper-nav-actions">
          {isLoggedIn ? (
            <>
              <button type="button" className="paper-nav-secondary" onClick={handleLogout}>Log Out</button>
              <Link to="/dashboard" className="paper-nav-primary">Dashboard</Link>
            </>
          ) : (
            <>
              <Link to="/login" className="paper-nav-secondary">Sign In</Link>
              <Link to="/register" className="paper-nav-primary">Sign Up</Link>
            </>
          )}
          <button type="button" className="paper-mobile-toggle" aria-label="Toggle navigation" onClick={() => setMobileOpen((open) => !open)}>
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      {mobileOpen ? (
        <div className="paper-mobile-menu">
          {[
            ['Product', productDropdownLinks],
            ['Resources', resourceDropdownLinks],
          ].map(([label, links]) => (
            <div className="paper-mobile-menu-group" key={label}>
              <strong>{label}</strong>
              {links.map((link) => (
                <NavHref key={link.label} href={link.href} onClick={() => setMobileOpen(false)}>
                  {link.label}
                </NavHref>
              ))}
            </div>
          ))}
          <Link to="/workflows" onClick={() => setMobileOpen(false)}>Workflows</Link>
          <Link to="/pricing" onClick={() => setMobileOpen(false)}>Pricing</Link>
          <Link to="/contact" onClick={() => setMobileOpen(false)}>Contact</Link>
          {isLoggedIn ? (
            <>
              <Link to="/dashboard" onClick={() => setMobileOpen(false)}>Dashboard</Link>
              <button type="button" className="paper-mobile-menu-logout" onClick={handleLogout}>Log Out</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setMobileOpen(false)}>Sign In</Link>
              <Link to="/register" onClick={() => setMobileOpen(false)}>Sign Up</Link>
            </>
          )}
        </div>
      ) : null}
    </header>
  );
}
